import { Box, Typography } from "@mui/material";
import { Linkedin, LucideProps, Mail, MapPin, Phone } from "lucide-react";
import { FC, ReactNode } from "react";

const BasicDetail = ({
  label,
  content,
  alwaysShow = false,
  horizontal = false,
}: {
  label: string;
  content: ReactNode | string | undefined;
  alwaysShow?: boolean;
  horizontal?: boolean;
}) => {
  if (!content && !alwaysShow) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: horizontal ? "row" : "column",
        gap: horizontal ? "8px" : "2px",
      }}
    >
      <Typography variant="bodyHeavy">{label}</Typography>
      {content ? (
        <Typography variant="body">{content}</Typography>
      ) : (
        <Typography variant="body" color="#8E9598">
          None provided
        </Typography>
      )}
    </Box>
  );
};

const ContactDetail = ({
  icon,
  content,
}: {
  icon: "Phone" | "Mail" | "MapPin" | "Linkedin" | undefined;
  content: ReactNode | string | undefined;
}) => {
  const contactIcons = {
    Phone: <Phone size={20} color={"#8E9598"} />,
    Mail: <Mail size={20} color={"#8E9598"} />,
    MapPin: <MapPin size={20} color={"#8E9598"} />,
    Linkedin: <Linkedin size={20} color={"#8E9598"} />,
  };

  return (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
      {icon && contactIcons[icon]}
      <Typography variant="body" sx={{ width: "100%" }}>
        {content}
      </Typography>
    </Box>
  );
};

const MultiSelectDetail = ({
  label,
  items,
}: {
  label: string;
  Icon: FC<LucideProps>;
  items: string[] | undefined;
}) => {
  if (!items || !items.length) return null;
  return (
    <BasicDetail
      label={label}
      content={
        <Box>
          {items
            .filter((v) => v)
            .map((value) => (
              <Typography
                variant="body"
                key={value}
                sx={{ display: "flex", gap: "2px" }}
              >
                • {value.replace(/-/g, " ")}
              </Typography>
            ))}
        </Box>
      }
    />
  );
};

const TrackingDetails = ({
  trackingParameters,
}: {
  trackingParameters: Record<string, string>;
}) => {
  const keys = Object.keys(trackingParameters || {});
  if (keys.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <Typography variant="bodyHeavy">Tracking</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          minWidth: 0,
          maxWidth: "400px",
        }}
      >
        {keys.map((key) => {
          const value = trackingParameters?.[key];
          if (!value) return null;
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                gap: "4px",
                minWidth: 0,
              }}
            >
              <Typography
                variant="bodySmallHeavy"
                sx={{ flexShrink: 0, color: "#6B6E7B" }}
              >
                {key}:
              </Typography>
              <Typography
                variant="bodySmall"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {value}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export { BasicDetail, ContactDetail, MultiSelectDetail, TrackingDetails };
